import { Injectable, OnDestroy } from '@angular/core';
import { Apollo } from 'apollo-angular';
import {
  GetUserPreferencesGQL,
  PrivacyConsentEnum,
  ThemeEnum,
  UpdateUserGQL,
  UpdateUserInput,
  UpdateUserPreferencesGQL,
  UserPreferences,
  UserPreferencesInput,
} from 'app/shared/notifications-api/graphql';
import { BehaviorSubject, interval, Observable, of, Subscription } from 'rxjs';
import { catchError, map, take, timeout } from 'rxjs/operators';
import Keycloak from 'keycloak-js';
import { SideNavComponent } from 'app/shared/components/side-nav/side-nav.component';
import { SidebarItem } from '@wind-log_library/fe-component-library';
import { DatePatternT, TimePatternT } from 'app/shared/services/date-time-format/date-time-format.service';
import { LanguageService } from 'app/shared/services/language/language.service';
import { cloneDeep } from 'lodash';
import { UserProfileService } from 'app/shared/user-profile/user-profile.service';

@Injectable({
  providedIn: 'root',
})
export class UserPreferencesService implements OnDestroy {
  private userPreferencesLoaded = new BehaviorSubject<boolean>(false);
  public module = '';
  private refreshTokenSub?: Subscription;

  private userPreferencesFallback: UserPreferences = {
    customDefaultModuleWind: 'overview',
    dateFormat: 'dd MMM yyyy',
    decimalFormat: '.',
    hourFormat: 'HH:mm',
    language: 'en',
    thousandFormat: "'",
    userId: '',
    siteTimezoneWind: localStorage.getItem('windlog-park-tz-preference'),
    portfolioTimezoneWind: localStorage.getItem('windlog-portfolio-tz-preference'),
    userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    privacyConsent: PrivacyConsentEnum.OptedOut,
    emailsNotificationsConsent: false
  };
  private userPreferences: UserPreferences;

  constructor(
    private apollo: Apollo,
    private updateUserPreferencesGQL: UpdateUserPreferencesGQL,
    private keycloakService: Keycloak,
    private userProfileService: UserProfileService,
    private getUserPreferencesGQL: GetUserPreferencesGQL,
    private updateUserGQL: UpdateUserGQL
  ) {
    this.loadUserPreferences();
  }

  public getUserPreferencesLoaded(): Observable<boolean> {
    return this.userPreferencesLoaded.asObservable();
  }

  private loadUserPreferences(): void {
    this.apollo
      .use('notifications_http')
      .query<{ userPreferences: UserPreferences }>({
        query: this.getUserPreferencesGQL.document,
        fetchPolicy: 'network-only',
      })
      .pipe(
        take(1),
        timeout(5000),
        catchError((error) => {
          // this.userPreferencesFallback.userId = this.keycloakService.getKeycloakInstance().tokenParsed?.sub || '';
          return of({ data: { userPreferences: this.userPreferencesFallback } });
        }),
      )
      .subscribe((response) => {
        const module = response?.data?.userPreferences?.customDefaultModuleWind;

        if (module) {
          localStorage.setItem('wind-custom-default-module', module);
        }

        this.userPreferences = response.data.userPreferences ? cloneDeep(response.data.userPreferences) : {userId: '0'};
        const lang = LanguageService.languages?.includes(response.data.userPreferences?.language) ? response.data.userPreferences?.language : 'en';
        this.userPreferences.language = lang;


        this.userPreferencesLoaded.next(true);
      });
  }

  public getUserPreferences(): UserPreferences {
    return this.userPreferences;
  }
  
  public updateUserInformation(input: UpdateUserInput): Observable<boolean> {
    return this.apollo
      .use('notifications_http')
      .mutate({
        mutation: this.updateUserGQL.document,
        variables: { input },
      })
      .pipe(
        catchError((error) => {
          console.error('Error updating user information:', error);
          return of(false);
        }),
        map((response: any) => {
          return response?.data?.updateUser?.success ?? false;
        })
      );
  }

  public updateUserPreferences(userPreferences: UserPreferencesInput): Observable<boolean> {
    return this.apollo
      .use('notifications_http')
      .mutate({
        mutation: this.updateUserPreferencesGQL.document,
        variables: { userPreferences },
      })
      .pipe(
        map((response: any) => {
          this.userPreferences = { ...this.userPreferences, ...response.data.updateUserPreferences, ...userPreferences };
          return true;
        })
      );
  }

  get language(): string {
    const lang = this.userPreferences?.language || localStorage.getItem('windlog_lang') || 'en';
    return lang.toLowerCase();
  }

  set language(lang: string) {
    this.updateUserPreferences({ language: lang.toLowerCase() }).subscribe();
  }

  get theme(): string {
    const theme = this.userPreferences?.theme || 'LIGHT';
    return theme.toLowerCase();
  }

  set theme(theme: string) {
    const themeEnumValue = theme.toUpperCase() as ThemeEnum;
    this.updateUserPreferences({ theme: themeEnumValue }).subscribe();
  }

  get thousandSeparator(): string {
    const separator = this.userPreferences?.thousandFormat || "'";
    return separator;
  }

  set thousandSeparator(separator: string) {
    this.updateUserPreferences({ thousandFormat: separator }).subscribe();
  }

  get decimalSeparator(): string {
    const separator = this.userPreferences?.decimalFormat || '.';
    return separator;
  }

  set decimalSeparator(separator: string) {
    this.updateUserPreferences({ decimalFormat: separator }).subscribe();
  }

  get portfolioTimezone(): string {
    const timezone = this.userPreferences?.portfolioTimezone;
    return timezone;
  }

  set portfolioTimezone(timezone: string) {
    this.updateUserPreferences({ portfolioTimezone: timezone }).subscribe();
  }

  get siteTimezone(): string {
    const timezone = this.userPreferences?.siteTimezone;
    return timezone;
  }

  set siteTimezone(timezone: string) {
    this.updateUserPreferences({ siteTimezone: timezone }).subscribe();
  }

  get sessionTimeout(): boolean {
    const hastimeout = this.userPreferences?.sessionTimeout || 'true';
    return hastimeout === 'true' || hastimeout === true;
  }

  set sessionTimeout(timeout: boolean) {
    this.updateUserPreferences({ sessionTimeout: timeout }).subscribe();
  }

  get customDefaultModule(): string {
    const module = this.userPreferences?.customDefaultModuleWind || 'overview';
    return module;
  }

  set customDefaultModule(module: string) {
    this.updateUserPreferences({ customDefaultModuleWind: module }).subscribe();
  }

  get dateFormat(): string {
    const pattern = this.userPreferences?.dateFormat || 'dd MMM yyyy';
    return pattern;
  }

  set dateFormat(pattern: string) {
    this.updateUserPreferences({ dateFormat: pattern }).subscribe();
  }

  get timeFormat(): string {
    const pattern = this.userPreferences?.hourFormat || 'HH:mm';
    return pattern;
  }

  set timeFormat(pattern: string) {
    this.updateUserPreferences({ hourFormat: pattern }).subscribe();
  }

  get selectedPortfolioId(): string {
    const portfolioId = localStorage.getItem('horizon-windlog_selected_portfolio_id');
    return portfolioId || '';
  }

  set selectedPortfolioId(portfolioId: string) {
    localStorage.setItem('horizon-windlog_selected_portfolio_id', portfolioId);
  }

  public refreshKeycloakToken(enable: boolean) {
    if (this.refreshTokenSub) {
      this.refreshTokenSub.unsubscribe();
      this.refreshTokenSub = undefined;
    }

    if (enable) {
      /* Every 1 hour, check if the token will expire within the next 6 hours. If so, refresh it */
      this.refreshTokenSub = interval(3600 * 1000).subscribe(() => {
        this.keycloakService.updateToken(21600);
      });
    }
  }

  public getDatePattern(): DatePatternT {
    return this.dateFormat as DatePatternT;
  }

  public getTimePattern(): TimePatternT {
    return this.timeFormat as TimePatternT;
  }

  public getAuthorizedModules(): SidebarItem[] {
    const excluded = ['module_operate_windlog', 'module_deice_windlog'];

    const authorizedModules = SideNavComponent.sidebarItemsRAW
      .filter((item) => !excluded.includes(item.module))
      .filter((item) => this.userProfileService.canAccessToModule(item.module));

    return authorizedModules;
  }

  public updateTimezonePreferences(userPreferences: {
    siteTimezoneWind?: string;
    portfolioTimezoneWind?: string;
    userTimezone?: string;
  }): Promise<boolean> {
    this.userPreferences = {
      ...this.userPreferences,
      ...userPreferences,
    };

    return this.apollo
      .use('notifications_http')
      .mutate({
        mutation: this.updateUserPreferencesGQL.document,
        variables: { userPreferences },
      })
      .pipe(
        take(1),
        catchError((error) => of({ data: null })),
        map((response) => !!response?.data?.updateUserPreferences)
      )
      .toPromise();
  }

  public getPreferences(): UserPreferences {
    return this.userPreferences || this.userPreferencesFallback;
  }


  ngOnDestroy(): void {
    if (this.refreshTokenSub) {
      this.refreshTokenSub.unsubscribe();
      this.refreshTokenSub = undefined;
    }
  }

}
